import dayjs, { Dayjs } from 'dayjs'
import duration, { Duration } from 'dayjs/plugin/duration'
import { useSearchParams } from 'react-router-dom'
import { AdOrOrganicFilterOption } from '../components/AdOrOrganicComboBox'
import { getClassifierSearchParam } from '../models/ClassifierSearchParam'
import { DmFilter, ReadFilterView, SocialMediaServiceView } from '../api/client'
import { useEffect, useState } from 'react'
import {
    ACCOUNTS_QUERY_PARAM,
    ADS_OR_ORGANIC_QUERY_PARAM,
    GENERAL_CLASSIFIER_QUERY_PARAM,
    KEYWORD_QUERY_PARAM,
    MODERATION_QUERY_PARAM,
    PERIOD_ABS_END_QUERY_PARAM,
    PERIOD_ABS_START_QUERY_PARAM,
    PERIOD_REL_DURATION_QUERY_PARAM,
    READ_RECEIPT_QUERY_PARAM,
    EXCLUDE_OWN,
    REPLIES,
    SOCIAL_MEDIA_SERVICE_QUERY_PARAM,
    CONTENT_FEED_ID_QUERY_PARAM,
    SUGGESTED_REPLIES,
    TEAMS_QUERY_PARAM,
    decodeIdsQueryParam,
    decodeStringsQueryParam,
    encodeIdsQueryParam,
    encodeStringQueryParam,
    DM_FILTER,
    AUTHOR_PROFILE_IDS_QUERY_PARAM
} from '../models/QueryParameters'
import SearchParameters from '../components/content/search/searchParameters'

dayjs.extend(duration)

export default function useContentSearchParams(): {
    searchParameters: SearchParameters
    onSearchParameterChange: (searchParameters: SearchParameters, replace?: boolean) => void
    allSocialMediaServices: Array<SocialMediaServiceView>

    setAllSocialMediaServices: (socialMediaServices: Array<SocialMediaServiceView>) => void
} {
    const [searchParams, setSearchParams] = useSearchParams()

    const [allSocialMediaServices, setAllSocialMediaServices] = useState<
        Array<SocialMediaServiceView>
    >([])

    //
    // Extract the query parameters from the URL
    //
    const accountIdsQueryString = searchParams.get(ACCOUNTS_QUERY_PARAM)
    const accountIds = decodeIdsQueryParam(accountIdsQueryString || '')

    const teamIdsQueryString = searchParams.get(TEAMS_QUERY_PARAM)
    const teamIds = decodeIdsQueryParam(teamIdsQueryString || '')

    const authorProfileIdsQueryString = searchParams.get(AUTHOR_PROFILE_IDS_QUERY_PARAM)
    const authorProfileIds = decodeIdsQueryParam(authorProfileIdsQueryString || '')

    const socialMediaServiceIdsQueryString = searchParams.get(SOCIAL_MEDIA_SERVICE_QUERY_PARAM)
    const socialMediaServiceIds = decodeIdsQueryParam(socialMediaServiceIdsQueryString || '')

    // Relative date period search params
    const timeDurationString = searchParams.get(PERIOD_REL_DURATION_QUERY_PARAM)
    const startDateTimeString = searchParams.get(PERIOD_ABS_START_QUERY_PARAM)
    const endDateTimeString = searchParams.get(PERIOD_ABS_END_QUERY_PARAM)

    const [timeDuration, setTimeDuration] = useState<Duration | undefined>(
        timeDurationString ? dayjs.duration(timeDurationString) : undefined
    )
    useEffect(() => {
        if (timeDurationString) {
            setTimeDuration(dayjs.duration(timeDurationString))
        } else setTimeDuration(undefined)
    }, [timeDurationString])

    // Absolute date period search params
    const [startDateTime, setStartDateTime] = useState<Dayjs | undefined>(
        startDateTimeString ? dayjs(startDateTimeString) : undefined
    )
    const [endDateTime, setEndDateTime] = useState<Dayjs | undefined>(
        endDateTimeString ? dayjs(endDateTimeString) : undefined
    )
    useEffect(() => {
        if (startDateTimeString) {
            setStartDateTime(dayjs(startDateTimeString))
        } else {
            setStartDateTime(undefined)
        }

        if (endDateTimeString) {
            setEndDateTime(dayjs(endDateTimeString))
        } else {
            setEndDateTime(undefined)
        }
    }, [startDateTimeString, endDateTimeString])

    const keyword = searchParams.get(KEYWORD_QUERY_PARAM) || ''
    const moderation = searchParams.get(MODERATION_QUERY_PARAM) || 'all'

    const contentFeedIdRaw = searchParams.get(CONTENT_FEED_ID_QUERY_PARAM)
    const contentFeedId = contentFeedIdRaw ? parseInt(contentFeedIdRaw, 10) : undefined

    // General classifiers
    const generalClassifierQueryString = searchParams.get(GENERAL_CLASSIFIER_QUERY_PARAM)
    const selectedGeneralClassifiers = decodeStringsQueryParam(generalClassifierQueryString || '')

    // Replies query parameters
    const suggestedReplies = searchParams.get(SUGGESTED_REPLIES) || ''
    const replies = searchParams.get(REPLIES) || ''

    // Ads or Organic query params
    const adOrOrganic =
        (searchParams.get(ADS_OR_ORGANIC_QUERY_PARAM) as AdOrOrganicFilterOption) || undefined

    // Read/Unread query param
    const readReceipt = (searchParams.get(READ_RECEIPT_QUERY_PARAM) as ReadFilterView) || undefined

    // DM Filter query param
    const dmFilter = (searchParams.get(DM_FILTER) as DmFilter) || 'COMMENTS_ONLY'

    // Has media query param
    const hasMediaRaw = searchParams.get('hasMedia')
    const hasMedia = hasMediaRaw === 'true' ? true : undefined

    // Exclude own query param
    let excludeOwnComments =
        searchParams.get(EXCLUDE_OWN) !== null &&
        searchParams.get(EXCLUDE_OWN)?.toLowerCase() === 'true'
    if (!excludeOwnComments) {
        // Not yet set in url - lets check local storage.
        const raw = localStorage.getItem(EXCLUDE_OWN)
        excludeOwnComments = raw !== null && raw.toLowerCase() === 'true'
    }

    // Update all parameters at once. Here, params are coming from changes to the
    // page, not changes to the query params.
    //
    // `replace` controls whether the update pushes a new history entry or replaces
    // the current one. Deliberate user changes push (so Back undoes them), but
    // mount-time default initialisation must replace: otherwise landing on a bare
    // /account/:id URL writes each default filter as its own history entry, stacking
    // several visually-identical dashboard pages so the browser Back button appears
    // to do nothing until pressed many times.
    function handleParameterChange(searchParameters: SearchParameters, replace: boolean = false) {
        const newSearchParams = new URLSearchParams(searchParams.toString())

        if (searchParameters.contentFeedId) {
            newSearchParams.set(
                CONTENT_FEED_ID_QUERY_PARAM,
                searchParameters.contentFeedId.toString()
            )
        } else {
            newSearchParams.delete(CONTENT_FEED_ID_QUERY_PARAM)
        }

        if (searchParameters.selectedGeneralClassifiers.length > 0) {
            newSearchParams.set(
                GENERAL_CLASSIFIER_QUERY_PARAM,
                encodeStringQueryParam(searchParameters.selectedGeneralClassifiers)
            )
        } else {
            newSearchParams.delete(GENERAL_CLASSIFIER_QUERY_PARAM)
        }

        // Time range search parameters ------------------------------------
        // timeDuration, startDateTime, and endDateTime are all related.
        // You can have either: timeDuration OR (startDateTime AND endDateTime),
        // so if one is set then the other(s) need to be unset.
        //
        // Notice that there's some precdence here: if startDateTime and endDateTime are set,
        // then timeDuration will be unset. This shouldn't be an issue because
        // DateTimePickerContainer.tsx handles all the setting of these values.
        // Ff anything weird every starts happening you should probably fix it there.
        if (searchParameters.timeDuration) {
            newSearchParams.set(
                PERIOD_REL_DURATION_QUERY_PARAM,
                searchParameters.timeDuration.toISOString()
            )
            newSearchParams.delete(PERIOD_ABS_START_QUERY_PARAM)
            newSearchParams.delete(PERIOD_ABS_END_QUERY_PARAM)
        } else {
            newSearchParams.delete(PERIOD_REL_DURATION_QUERY_PARAM)
        }
        if (searchParameters.startDateTime) {
            newSearchParams.set(
                PERIOD_ABS_START_QUERY_PARAM,
                String(searchParameters.startDateTime.toISOString())
            )
            newSearchParams.delete(PERIOD_REL_DURATION_QUERY_PARAM)
        } else {
            newSearchParams.delete(PERIOD_ABS_START_QUERY_PARAM)
        }
        if (searchParameters.endDateTime) {
            newSearchParams.set(
                PERIOD_ABS_END_QUERY_PARAM,
                String(searchParameters.endDateTime.toISOString())
            )
            newSearchParams.delete(PERIOD_REL_DURATION_QUERY_PARAM)
        } else {
            newSearchParams.delete(PERIOD_ABS_END_QUERY_PARAM)
        }
        // END time range search parameters ------------------------------------

        if (searchParameters.selectedSocialMediaServices) {
            newSearchParams.set(
                SOCIAL_MEDIA_SERVICE_QUERY_PARAM,
                encodeIdsQueryParam(
                    searchParameters.selectedSocialMediaServices.map((service) => service.id)
                )
            )
        } else {
            newSearchParams.delete(SOCIAL_MEDIA_SERVICE_QUERY_PARAM)
        }

        if (searchParameters.selectedTeams.length > 0) {
            newSearchParams.set(
                TEAMS_QUERY_PARAM,
                encodeIdsQueryParam(searchParameters.selectedTeams)
            )
        } else {
            newSearchParams.delete(TEAMS_QUERY_PARAM)
        }

        if (searchParameters.authorProfileIds && searchParameters.authorProfileIds.length > 0) {
            newSearchParams.set(
                AUTHOR_PROFILE_IDS_QUERY_PARAM,
                encodeIdsQueryParam(searchParameters.authorProfileIds)
            )
        } else {
            newSearchParams.delete(ACCOUNTS_QUERY_PARAM)
        }

        if (searchParameters.selectedAccountIds.length > 0) {
            newSearchParams.set(
                ACCOUNTS_QUERY_PARAM,
                encodeIdsQueryParam(searchParameters.selectedAccountIds)
            )
        } else {
            newSearchParams.delete(ACCOUNTS_QUERY_PARAM)
        }

        if (searchParameters.moderation.length > 0) {
            newSearchParams.set(MODERATION_QUERY_PARAM, searchParameters.moderation)
        } else {
            newSearchParams.delete(MODERATION_QUERY_PARAM)
        }

        if (searchParameters.keyword.length > 0) {
            newSearchParams.set(KEYWORD_QUERY_PARAM, searchParameters.keyword)
        } else {
            newSearchParams.delete(KEYWORD_QUERY_PARAM)
        }

        if (searchParameters.selectedSuggestedReplies === 'true') {
            newSearchParams.set(SUGGESTED_REPLIES, searchParameters.selectedSuggestedReplies)
        } else {
            newSearchParams.delete(SUGGESTED_REPLIES)
        }

        if (searchParameters.selectedReplies) {
            newSearchParams.set(REPLIES, searchParameters.selectedReplies)
        } else {
            newSearchParams.delete(REPLIES)
        }

        if (searchParameters.adOrOrganic) {
            newSearchParams.set(ADS_OR_ORGANIC_QUERY_PARAM, searchParameters.adOrOrganic)
        } else {
            newSearchParams.delete(ADS_OR_ORGANIC_QUERY_PARAM)
        }

        if (searchParameters.readReceipt) {
            newSearchParams.set(READ_RECEIPT_QUERY_PARAM, searchParameters.readReceipt)
        } else {
            newSearchParams.delete(READ_RECEIPT_QUERY_PARAM)
        }

        if (searchParameters.dmFilter) {
            newSearchParams.set(DM_FILTER, searchParameters.dmFilter)
        } else {
            newSearchParams.delete(DM_FILTER)
        }

        if (searchParameters.hasMedia) {
            newSearchParams.set('hasMedia', 'true')
        } else {
            newSearchParams.delete('hasMedia')
        }

        newSearchParams.set(EXCLUDE_OWN, String(searchParameters.excludeOwnComments))
        // Lets also set this in local storage.
        localStorage.setItem(EXCLUDE_OWN, String(searchParameters.excludeOwnComments))

        setSearchParams(newSearchParams, { replace })
    }

    return {
        searchParameters: {
            contentFeedId,
            selectedAccountIds: accountIds,
            authorProfileIds: authorProfileIds,
            selectedTeams: teamIds,
            selectedSocialMediaServices: allSocialMediaServices.filter((service) =>
                socialMediaServiceIds.includes(service.id)
            ),
            timeDuration,
            startDateTime,
            endDateTime,
            keyword,
            moderation,

            selectedSuggestedReplies: suggestedReplies,
            selectedReplies: replies,

            selectedGeneralClassifiers,

            selectedClassifiers: selectedGeneralClassifiers.map((classifier) =>
                getClassifierSearchParam(classifier)
            ),

            adOrOrganic,
            readReceipt,
            // Default value
            owned: true,
            excludeOwnComments,
            dmFilter,
            hasMedia
        },
        onSearchParameterChange: handleParameterChange,
        allSocialMediaServices,
        setAllSocialMediaServices
    }
}
